import { Component, Input, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IconComponent } from 'continity-shared-ui';

import { PHOENIX_CUSTOM_ICONS } from './phoenix-custom-icons';

/**
 * APP-LOCAL icon wrapper (AB#2032 — INTERIM). Renders a Phoenix-only custom glyph from
 * {@link PHOENIX_CUSTOM_ICONS} when the name is registered there, and otherwise DELEGATES to
 * `continity-shared-ui`'s `app-icon` — so it is a drop-in superset of `app-icon` for the Phoenix portal.
 *
 * `name` is deliberately typed `string` (not shared-ui's `IconName`) so custom names outside the shared union
 * are allowed; the shared-ui delegation casts via `$any`.
 *
 * The custom SVG is injected via `[innerHTML]` bound to a `DomSanitizer.bypassSecurityTrustHtml` value —
 * Angular strips inline `<svg>` from a raw string `innerHTML` binding otherwise. The source is our OWN static
 * constant map (no user input), so the bypass is safe.
 *
 * 🚧 INTERIM: fold these custom icons into `continity-shared-ui` later (Follow-up AB#2032); until then add new
 * Phoenix glyphs to {@link PHOENIX_CUSTOM_ICONS}.
 */
@Component({
  selector: 'phoenix-icon',
  standalone: true,
  imports: [IconComponent],
  template: `
    @if (customSvg(); as svg) {
      <span
        class="phoenix-icon"
        [style.width.px]="size"
        [style.height.px]="size"
        [innerHTML]="svg"></span>
    } @else {
      <app-icon [name]="$any(name)" [size]="size"></app-icon>
    }
  `,
  styles: [
    `
      /* Mirror shared-ui app-icon's box model so custom glyphs align identically in a
         flex row: without a flex :host the inline host introduces a baseline strut that
         pushes the glyph up (~descender space) next to the nav label. */
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .phoenix-icon {
        display: inline-flex;
        line-height: 0;
      }
      /* The SVG is injected via [innerHTML] (outside view encapsulation) → ::ng-deep. */
      :host ::ng-deep svg {
        display: block;
      }
    `,
  ],
})
export class PhoenixIconComponent {
  private readonly sanitizer = inject(DomSanitizer);

  /** Icon name — a {@link PHOENIX_CUSTOM_ICONS} key renders the custom SVG; anything else delegates to `app-icon`. */
  @Input() name = '';

  /** Rendered size in px (both custom SVG wrapper + delegated `app-icon`). */
  @Input() size = 18;

  /** The trusted custom SVG for {@link name}, or null when it is a shared-ui icon (delegate to `app-icon`). */
  customSvg(): SafeHtml | null {
    const raw = PHOENIX_CUSTOM_ICONS[this.name];
    return raw ? this.sanitizer.bypassSecurityTrustHtml(raw) : null;
  }
}
